import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { Header } from "../components/Header";
import { B2CProfileModals } from "../components/profile/B2CProfileModals";
import { ProfileImageUploader } from "../components/profile/ProfileImageUploader";
import { ProfileJourneyWizard } from "../components/profile/ProfileJourneyWizard";
import { useUserData } from "../contexts/user-data-context";
import { useB2CProfileModals } from "../hooks/useB2CProfileModals";
import { useUserType } from "../hooks/useUserType";
import { useAnalytics } from "../lib/analytics";
import { useClientTranslations } from "../lib/i18n/client";
import { profileSections } from "../lib/profile/b2cProfileSections";
import styles from "./ProfilePage.module.css";

const titleKeyById: Record<string, string> = {
	"name-location": "sections.nameLocation",
	"birth-year-gender": "sections.birthYearGender",
	"current-status": "sections.currentStatus",
	"fertility-role": "sections.fertilityRole",
	"journey-type": "sections.journeyType",
	"additional-medical-info": "sections.additionalMedicalInfo",
	clinic: "sections.clinic",
};

// The three journey-related rows all launch the branching journey sub-flow.
// They always start at "Current status" so the flow's prerequisites (status →
// role → journey) are set in order — this avoids opening a screen the machine
// would skip when status/role isn't set yet (which previously bounced straight
// back to Profile). These rows are always shown; the others open their modal.
const JOURNEY_SECTION_IDS = new Set([
	"current-status",
	"fertility-role",
	"journey-type",
]);

export function ProfilePage() {
	const { user } = useAuth0();
	const { userData } = useUserData();
	const { t, direction } = useClientTranslations("profile");
	const { logProfile } = useAnalytics();
	const { isD2C } = useUserType();

	const {
		openModalId,
		modalAnswers,
		openModal,
		closeModal,
		handleInputChange,
		handleSave,
		initializeAnswers,
	} = useB2CProfileModals();

	const [isJourneyWizardOpen, setIsJourneyWizardOpen] = useState(false);

	// 86exzdxyt — D2C users have no clinic/provider, so hide the "Your Clinic"
	// section from their profile. All journey rows stay shown (the sub-flow
	// handles a missing status itself by starting at Current status).
	const visibleSections = isD2C
		? profileSections.filter((section) => section.id !== "clinic")
		: profileSections;

	useEffect(() => {
		logProfile("page_viewed");
	}, [logProfile]);

	useEffect(() => {
		initializeAnswers();
	}, [initializeAnswers]);

	const getInitialImageUrl = () => {
		if (userData?.userImage) return userData.userImage;
		if (user?.picture) return user.picture;
		return "";
	};

	return (
		<div className={styles.wrapper}>
			<Header />
			<main className={styles.main} dir={direction}>
				<div className={styles.container}>
					<div className={styles.heading}>
						<h1 className={styles.title}>{t("profile.pageTitle")}</h1>
						<p className={styles.subtitle}>{t("profile.pageSubtitle")}</p>
					</div>

					<div className={styles.content}>
						<ProfileImageUploader
							initialImageUrl={getInitialImageUrl()}
							user={userData ? { id: userData.id } : undefined}
						/>

						<div className={styles.sections}>
							{visibleSections.map((section) => (
								<button
									key={section.id}
									type="button"
									className={styles.sectionCard}
									onClick={() => {
										logProfile("personal_details_section_clicked", {
											section: section.id,
										});
										if (JOURNEY_SECTION_IDS.has(section.id)) {
											setIsJourneyWizardOpen(true);
										} else {
											openModal(section.id);
										}
									}}
								>
									<span className={styles.sectionLabel}>
										{t(titleKeyById[section.id] || section.id)}
									</span>
									<svg
										className={styles.editIcon}
										viewBox="0 0 20 20"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<title>{t("common.edit")}</title>
										<path
											d="M13.586 3.586a2 2 0 112.828 2.828l-9.5 9.5-3.5 1 1-3.5 9.172-9.828z"
											stroke="currentColor"
											strokeWidth="1.2"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
										<path
											d="M12.5 4.5l3 3"
											stroke="currentColor"
											strokeWidth="1.2"
											strokeLinecap="round"
										/>
									</svg>
								</button>
							))}
						</div>
					</div>
				</div>

				<B2CProfileModals
					openModalId={openModalId}
					modalAnswers={modalAnswers}
					onClose={closeModal}
					onInputChange={handleInputChange}
					onSave={handleSave}
				/>
				{isJourneyWizardOpen && (
					<ProfileJourneyWizard
						startScreen="status"
						onClose={() => setIsJourneyWizardOpen(false)}
					/>
				)}
			</main>
		</div>
	);
}
